// import './../../App.css';

import pako from 'pako';
import { useState, useEffect, useRef, useContext, useCallback, createRef } from 'react';
import { Box, Button, CircularProgress, Grid, Paper, Stack, Typography } from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// import { ALLOWED_TAG_LIST, Interweave } from 'interweave';

// import { ErrorBox, makePostRequest } from 'revelata-common-ui';
import { ErrorBox } from '../ErrorBox';
import { makePostRequest } from '../RestApi';


import { DisplayedCikContext, SelectedDatapointContext } from '../Contexts';
import { longestPrefixInString } from '../Utils';
// import { longestPrefixInString } from 'revelata-common-ui';
import DOMPurify from 'dompurify';

// Set up PDF.js worker - use local file
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

// Info bar shown above the filing 
function InfoBar({cik, selectedDatapoint, zoom, onZoomIn, onZoomOut, onZoomReset, data}) {        

    if (selectedDatapoint && selectedDatapoint['dp']) {
        const dp = selectedDatapoint['dp'];       
        const displayFormDocType = dp['formType'] + (dp['formType'] === dp['docType'] ? "" : " (" + dp['docType'] + ")")
        const displayFormLabel = dp['formType'] === dp['docType'] ? "Form Type" : "Form Type (Document Type)"
        return (
            <Grid container 
                height="44px" 
                minHeight="44px" 
                direction="column"
                alignItems="center" 
                justifyContent="center" 
                sx={{p: "8px", border: "solid", borderWidth: "0px", width: "100%"}}>                               
                {dp['formType'] && (
                    <Grid item xs="auto" align="center" sx={{ border: "solid", borderWidth: "0px", mx: 1}}> 
                        <Typography variant="h7"  color="var(--brand-main)" sx={{fontSize: "0.75rem", fontWeight: "700"}}>{displayFormLabel}</Typography>
                        <Typography variant="h6"  sx={{fontSize: "0.825rem", fontWeight: "700"}}>{displayFormDocType}</Typography>
                    </Grid>
                )}
                {dp['filingDate'] && (
                    <Grid item xs="auto" align="center" sx={{ border: "solid", borderWidth: "0px", mx: 1}}>                     
                        <Typography variant="h7"  color="var(--brand-main)" sx={{fontSize: "0.75rem", fontWeight: "700"}}>Filing Date</Typography>
                        <Typography variant="h6"  sx={{fontSize: "0.825rem", fontWeight: "700"}}>{dp['filingDate']}</Typography>
                    </Grid>
                )}
                {dp['reportingPeriod'] && (
                    <Grid item xs="auto" align="center" sx={{ border: "solid", borderWidth: "0px", mx: 1}}> 
                        <Typography variant="h7"  color="var(--brand-main)" sx={{fontSize: "0.75rem", fontWeight: "700"}}>Reporting Period</Typography>
                        <Typography variant="h6"  sx={{fontSize: "0.825rem", fontWeight: "700"}}>{dp['reportingPeriod']}</Typography>
                    </Grid>
                )}
                {data && data.type === 'pdf_page' && (
                    <Grid item xs="auto" align="center" sx={{ border: "solid", borderWidth: "0px", mx: 1}}> 
                        {/* Zoom Controls */}
                        <Box sx={{ 
                            display: 'flex',
                            gap: 1,
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                            <Button 
                                size="small" 
                                variant="contained" 
                                onClick={onZoomOut}
                                sx={{ 
                                    minWidth: 'auto', 
                                    padding: '2px 6px',
                                    backgroundColor: '#666',
                                    color: 'white',
                                    fontSize: '10px',
                                    '&:hover': {
                                        backgroundColor: '#888'
                                    }
                                }}
                            >
                                -
                            </Button>
                            <Button 
                                size="small" 
                                variant="contained" 
                                onClick={onZoomReset}
                                sx={{ 
                                    minWidth: 'auto', 
                                    padding: '2px 6px',
                                    backgroundColor: '#666',
                                    color: 'white',
                                    fontSize: '10px',
                                    '&:hover': {
                                        backgroundColor: '#888'
                                    }
                                }}
                            >
                                {Math.round(zoom * 100)}%
                            </Button>
                            <Button 
                                size="small" 
                                variant="contained" 
                                onClick={onZoomIn}
                                sx={{ 
                                    minWidth: 'auto', 
                                    padding: '2px 6px',
                                    backgroundColor: '#666',
                                    color: 'white',
                                    fontSize: '10px',
                                    '&:hover': {
                                        backgroundColor: '#888'
                                    }
                                }}
                            >
                                +
                            </Button>
                        </Box>
                        

                    </Grid>
                )}
            </Grid>
        )
    }
    return "Source Filing"
}

// export const ProvenanceCikContext = createContext();
// export const ProvenanceDatapointContext = createContext();

export function ProvenancePane({provenancePaneHeight, noSelectionComponent }) {    
    // const [accessToken, setAccessToken] = useState(null);
    
    const [data, setData] = useState(null);
    const [apiWait, setApiWait] = useState(false);
    const [errorState, setErrorState] = useState(false);
    
    // PDF viewer state
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [parsedProvenance, setParsedProvenance] = useState(null);
    const [highlightBox, setHighlightBox] = useState(null);
    const pageRefs = useRef({});
    const [pageWidth, setPageWidth] = useState(595); // Default A4 width
    const [pageHeight, setPageHeight] = useState(841); // Default A4 height
    const [zoom, setZoom] = useState(1.0); // Zoom level
    const [hasSetInitialZoom, setHasSetInitialZoom] = useState(false);
    const [isUserZooming, setIsUserZooming] = useState(false);
    


    // Add ref for the scrollable Box
    const scrollableBoxRef = useRef(null);
    const documentContainerRef = useRef(null);
    const pdfContainerRef = useRef(null);
    const pageElementRef = useRef(null);

    // Handle provenance parsing when PDF data is ready
    useEffect(() => {
        if (data && data.type === 'pdf_page' && parsedProvenance && parsedProvenance.bbox) {
            try {
                let bboxParts = null;
                // Parse the JSON array format: "[637.7952270508, 693.2633056641, 889.4184570312, 716.2388916016]"
                if (typeof parsedProvenance.bbox === 'string') {
                    bboxParts = JSON.parse(parsedProvenance.bbox);
                } else {
                    bboxParts = parsedProvenance.bbox;
                }

                if (bboxParts.length === 4) {
                    const highlightData = {
                        x: bboxParts[0],
                        y: bboxParts[1],
                        width: bboxParts[2] - bboxParts[0],
                        height: bboxParts[3] - bboxParts[1]
                    };
                    setHighlightBox(highlightData);
                } else {
                    console.warn("Invalid bbox length:", bboxParts.length);
                }
            } catch (error) {
                console.error("Error parsing bbox:", parsedProvenance.bbox, error);
            }
        }
    }, [data, parsedProvenance]);

    const [displayedCik, setDisplayedCik] = useContext(DisplayedCikContext);
    const [selectedDatapoint, setSelectedDatapoint ] = useContext(SelectedDatapointContext);
    // const [fromDate, toDate] = useContext(DateRangeContext);
    // const [pv, setPv] = useState(['/html/body/div[3]/div/p[4]', '/html/body/div[3]/div/p[5]']);
    // const [pv, setPv] = useState([]);
    // const [targetSent, setTargetSent] = useState('Accordingly, the total royalty payments received by Mesabi Trust on July 30, 2020 from Cliffs were $4,349,830. The royalties paid to Mesabi Trust are based on the volume of shipments of iron ore pellets for the quarter and the year to date, the pricing of iron ore product sales, and the percentage of iron ore pellet shipments from Mesabi Trust lands rather than from non-Mesabi Trust lands.')    
    // console.log(`PANE with ${displayedCik}!`, 'WAIT: ',apiWait )

    const autoScrollCallback = useCallback(
        (node) => {
            node?.scrollIntoView({
                behavior: "smooth",
                block: "start",
                inline: "center"
            });
        }, []
    );

    useEffect(() => {
        // Scroll to the element with the ID from the fragment identifier
        if (data) {
          const element = document.getElementById('revelata-highlight-0');
          if (element) {
            element.scrollIntoView({ behavior: 'instant',block: "start",
                inline: "center" })
          }
        }
      }, [data])

    // // Set default scroll position when component mounts or data changes
    // useEffect(() => {
    //     if (scrollableBoxRef.current) {
    //         // Set scroll position to top-left (0, 0)
    //         scrollableBoxRef.current.scrollTop = 0;
    //         scrollableBoxRef.current.scrollLeft = 0;
            
    //         // Alternative: Set to specific position
    //         // scrollableBoxRef.current.scrollTop = 100; // 100px from top
    //         // scrollableBoxRef.current.scrollLeft = 50;  // 50px from left
            
    //         // Alternative: Set to center
    //         // const container = scrollableBoxRef.current;
    //         // container.scrollTop = (container.scrollHeight - container.clientHeight) / 2;
    //         // container.scrollLeft = (container.scrollWidth - container.clientWidth) / 2;
    //     }
    // }, [data]); // Re-run when data changes

    // Reset PDF state when selectedDatapoint changes
    useEffect(() => {
        // Reset zoom state when selectedDatapoint changes
        setHasSetInitialZoom(false);
        
        if (!selectedDatapoint) {
            setPdfUrl(null);
            setParsedProvenance(null);
            setHighlightBox(null);
            setPageNumber(1);
            setNumPages(null);
        } else {
            // Reset highlight box when selectedDatapoint changes
            setHighlightBox(null);
        }
    }, [selectedDatapoint, pdfUrl]);




    // When selectedDatapoint changes, hit the API or clear the display
    useEffect(        
        () => {
                        
            // Don't do anything until these two are in sync:
            if (selectedDatapoint) {         

                setData(null);     // clears the display
                setApiWait(true);  // set waiting state to show spinners 
                setErrorState(false); // clear error state                                

                let parsedProvenance = null;

                if (!selectedDatapoint['dp']['bbox']) {
                    const acc_no = selectedDatapoint['dp']['accNo'];

                    // Parse the packed acc_no field: {company_id}_{doc_id}_{page_idx}_{bbox}
                    try {
                        const parts = acc_no.split('_');
                        if (parts.length >= 4) {
                            parsedProvenance = {
                                company_id: parts[0],
                                doc_id: parts[1],
                                page_idx: parseInt(parts[2]),
                                bbox: parts[3]
                            };
                            setParsedProvenance(parsedProvenance);
                        } else {
                            console.warn("Invalid acc_no format:", acc_no);
                        }
                    } catch (error) {
                        console.error("Error parsing acc_no:", acc_no, error);
                    }
                }
                else {
                    parsedProvenance = {
                        company_id: selectedDatapoint['dp']['company_id'],
                        doc_id: selectedDatapoint['dp']['doc_id'],
                        page_idx: parseInt(selectedDatapoint['dp']['page_idx']),
                        bbox: selectedDatapoint['dp']['bbox']
                    };
                    setParsedProvenance(parsedProvenance);
                }
                
                const requestPayload = {
                    'company_id': parsedProvenance['company_id'],
                    'document_id': parsedProvenance['doc_id'],
                    'page_idx': parsedProvenance['page_idx'] + 1
                };

                // If company_id is fewer than five digits, pad with leading zeros
                if (requestPayload['company_id'] && requestPayload['company_id'].length < 5) {
                    requestPayload['company_id'] = requestPayload['company_id'].padStart(5, '0');
                }
                s3://com.revelata.dev.infra.hk/hk_test/00002/annualReport/11561740.pdf
                // console.log("-----------------DOC API!-----------------")
                
                // THIS MUST NOT BE A CALL TO authenticatedPostRequest BECAUSE THIS SERVICES
                // PUBLICLY ACCESSIBLE PROVENANCE DATA.
                makePostRequest(process.env.REACT_APP_API_BASE_URL + '/document', null, requestPayload)
                    .then(
                        response => {
                            
                            // Handle the new response format with PDF page data
                            if (response['pdf_page']) {
                                
                                // Decode and decompress the PDF page
                                try {
                                    const compressedData = atob(response['pdf_page']);
                                    const uint8Array = new Uint8Array(compressedData.split('').map(c => c.charCodeAt(0)));
                                    const pdfData = pako.inflate(uint8Array);
                                                                        
                                                                    // Convert Uint8Array to ArrayBuffer for react-pdf
                                const arrayBuffer = pdfData.buffer.slice(pdfData.byteOffset, pdfData.byteOffset + pdfData.byteLength);
                                                                
                                setData({ 
                                    type: 'pdf_page', 
                                    pdfData: arrayBuffer
                                });
                                    
                                } catch (error) {
                                    console.error('Error processing PDF page:', error);
                                    setErrorState(true);
                                }
                            } else {
                                console.error("Unexpected response format:", response);
                                setErrorState(true);
                            }
                            
                            setApiWait(false); // Got response, so no longer waiting
                            setErrorState(false);
                        }
                    )
                    .catch(error => {
                        console.log("API ERROR: ", error)
                        setErrorState(true);
                    })
            } else {
                // If null selectedDatapoint, clear & reset (e.g., when user runs new search).
                setData(null);
                setApiWait(false);
                setErrorState(false);
            }
        }, 
        [displayedCik, selectedDatapoint]
    );

    // useEffect( 
    //     () => {
    //         console.log("data changed: ", data);
    //     }, [data]
    // )

    // Error handler
    if (errorState) {
        return <ErrorBox />
    }

    // Show spinner when waiting for API response
    if (apiWait && data === null) {
        return (
            <Stack alignItems="center" justifyContent="center" height="100%">
                <CircularProgress disableShrink={true} />
            </Stack>
        )
    }    

    // If no selected datapoint, then show nothing
    if (data === null) {
        return noSelectionComponent
    } 

    // Otherwise, we have the API response and a selected datapoint, so display the provenance
    // document

    // PDF viewer functions
    const onDocumentLoadSuccess = (pdf) => {
        // Handle numPages if it's the old format
        if (pdf.numPages) {
            setNumPages(pdf.numPages);
            
            // Navigate to the target page if specified
            if (parsedProvenance && parsedProvenance.page_idx && parsedProvenance.page_idx <= pdf.numPages) {
                setPageNumber(parsedProvenance.page_idx);
            }
        }
        
        // Get the first page dimensions when the PDF document loads
        pdf.getPage(1).then(page => {
            const viewport = page.getViewport({scale: 1.0});
            setPageWidth(viewport.width);
            setPageHeight(viewport.height);
        });
    };




    const handleZoomIn = () => {
        setZoom(prevZoom => Math.min(prevZoom * 1.2, 3.0));
    };

    const handleZoomOut = () => {
        setZoom(prevZoom => Math.max(prevZoom / 1.2, 0.25));
    };

    const handleZoomReset = () => {
        setZoom(1.0);
    };



    const renderHighlightBox = () => {

        if (!highlightBox) {
            console.log("No highlight box data available");
            console.log(selectedDatapoint);
            return null;
        }

        if (highlightBox.x <= 1 && highlightBox.y <= 1 && highlightBox.width <= 1 && highlightBox.height <= 1)
        {
            highlightBox.x *= pageWidth;
            highlightBox.y *= pageHeight;
            highlightBox.width *= pageWidth;
            highlightBox.height *= pageHeight;
        }

        // Apply zoom scaling to highlight box
        const scaledHighlightBox = {
            x: highlightBox.x,
            y: highlightBox.y,
            width: highlightBox.width,
            height: highlightBox.height
        };
        
        // console.log("Highlight box coordinates:", {
        //     x: scaledHighlightBox.x,
        //     y: scaledHighlightBox.y,
        //     width: scaledHighlightBox.width,
        //     height: scaledHighlightBox.height
        // });
        
        if (selectedDatapoint && selectedDatapoint['dp']['citationType'] && selectedDatapoint['dp']['citationType'] === 'table') {

            scaledHighlightBox.x = 0;
            scaledHighlightBox.width = pageWidth;


            return <div
                style={{
                    position: 'absolute',
                    left: `${scaledHighlightBox.x}px`,
                    top: `${scaledHighlightBox.y}px`,
                    width: `${scaledHighlightBox.width}px`,
                    height: `${scaledHighlightBox.height}px`,
                    backgroundColor: 'rgba(255, 255, 0, 0.3)',
                    mixBlendMode: 'multiply',
                    pointerEvents: 'none',
                    zIndex: 1000, 
                }}
             />
         }
        return (
            <div
                style={{
                    position: 'absolute',
                    left: `${scaledHighlightBox.x}px`,
                    top: `${scaledHighlightBox.y}px`,
                    width: `${scaledHighlightBox.width}px`,
                    height: `${scaledHighlightBox.height}px`,
                    backgroundColor: 'rgba(255, 255, 0, 0.3)',
                    mixBlendMode: 'multiply',
                    pointerEvents: 'none',
                    zIndex: 1000, 
                }}
            />
        );
    };

    // Render based on data type
    const renderContent = () => {
        
        if (data && data.type === 'pdf_page' && data.pdfData) {

            if (documentContainerRef.current && highlightBox && data.pdfData && !hasSetInitialZoom) {
                // Calculate zoom to fit page height to container height (only first time)
                const containerHeight = documentContainerRef.current.clientHeight - 32; // Subtract padding
                const fitZoom = containerHeight/pageHeight;

                setZoom(fitZoom);
                setHasSetInitialZoom(true);

                console.log("Zoom: ", zoom, highlightBox.x, pageWidth / 2);
                // Auto-scroll to highlight box if it's positioned beyond the middle of the visible area
                if (pdfContainerRef.current && highlightBox && zoom > 0) {
                    const container = pdfContainerRef.current;
                    
                    // Check if highlight box is on the right side and scroll immediately
                    if (highlightBox.x > pageWidth / 2) {
                        container.scrollLeft = container.scrollWidth - container.clientWidth;
                    }
                }
            }
            return (
                <Box 
                    ref={pdfContainerRef}
                    sx={{ 
                        width: `${pageWidth * zoom}px`, // Limit max width to 800px (A4 is 595px at 1x)
                        height: `${pageHeight * zoom}px`,
                        position: 'relative',
                        overflow: "visible",
                        maxWidth: '100%',
                        maxHeight: '100%'}}

                >
                    <Box sx={{ 
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        transform: `scale(${zoom})`, 
                        transformOrigin: 'top left'
                    }}>
                        <Document file={data.pdfData} onLoadSuccess={onDocumentLoadSuccess}>
                            <Page 
                                ref={pageElementRef}
                                pageNumber={1} 
                            />
                        </Document>
                    </Box>
                    <Box sx={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        pointerEvents: 'none',
                        transform: `scale(${zoom})`,
                        transformOrigin: 'top left'
                    }}>
                        {renderHighlightBox()}
                    </Box>
                </Box>
            );
        } else {
            return (
                <Box sx={{ p: 2, width: '100%', textAlign: 'center' }}>
                    <Typography variant="body1">
                        No document content available.
                    </Typography>
                </Box>
            );
        }
    };
    
    return (
        <Box sx={{display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '100%', maxHeight: '100%', ml: 0, mr: 0, justifyContent: 'flex-start', alignItems: 'center', overflow: 'visible'}}>
            <Paper sx={{width: '90%', maxWidth: '90%', borderBottom: 1, borderColor: "grey.500", padding: "0px", backgroundColor: "var(--mui-palette-background-default)", flexShrink: 0}}>
                <InfoBar cik={displayedCik} selectedDatapoint={selectedDatapoint} zoom={zoom} onZoomIn={handleZoomIn} onZoomOut={handleZoomOut} onZoomReset={handleZoomReset} data={data}/>
            </Paper>            
            <Box ref={documentContainerRef} sx={{ 
                p: 2, 
                width:'100%', 
                display: 'flex', 
                alignItems: 'flex-start', 
                justifyContent: 'center', 
                color: "black", 
                flex: '1 1 auto', 
                minHeight: 0, 
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                    width: '12px',
                    height: '12px',
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f1f1f1',
                    borderRadius: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#888',
                    borderRadius: '6px',
                    '&:hover': {
                        backgroundColor: '#666',
                    },
                },
                '&::-webkit-scrollbar-corner': {
                    backgroundColor: '#f1f1f1',
                }
            }}>                    
                {renderContent()}
            </Box>                
        </Box>
    );
};

export default ProvenancePane;

// /html/body/div[3]/div/p[4]


